import { type KeyValuePairValidationFn, type KeyValueValidation } from '@h2oai/ui-kit';
import { useMemo } from 'react';

import { AIEMOpType, AIEngine } from '../../../../../aiem/engine/types';
import {
  DAIEngineProfile,
  DAIEngineProfile_ConfigEditability,
} from '../../../../../aiem/gen/ai/h2o/engine/v1/dai_engine_profile_pb';
import { EngineProfileEntityModel } from '../../../../../pages/AIEngineSettingsPage/components/ListWithCollapsedSettings/types';
import { validateId } from '../../../utils';

/**
 * Hook for handling all validation logic for engine configuration.
 *
 * Responsibilities:
 * - Validates engine state based on constraints and operation type
 * - Provides advanced configuration validation for DAI engines
 * - Checks memory constraints against profile limits
 * - Validates required fields for create vs update operations
 * - Manages configuration editability settings
 *
 * Validation Rules:
 * - Create: Requires displayName, valid ID, version, and memoryBytes within constraints
 * - Update: Requires displayName and version
 * - Advanced Config: Validates key-value pairs for DAI engines with full editability
 *
 * @param engineState - Current engine state being validated
 * @param selectedProfileOption - Selected profile with constraints
 * @param versionKey - Key for accessing version in engine state
 * @param op - Operation type (create, update, view)
 * @returns Object containing validation state and helper functions
 */
export const useEngineValidation = (
  engineState: AIEngine,
  selectedProfileOption?: EngineProfileEntityModel,
  versionKey?: string,
  op?: AIEMOpType
) => {
  const isEngineValid = useMemo(() => {
    if (!selectedProfileOption || !versionKey) return false;

    const { displayName, id, memoryBytes } = engineState;
    const version = engineState[versionKey as keyof AIEngine];

    if (op === AIEMOpType.create) {
      const containsValues = Boolean(displayName && validateId(id) && version && memoryBytes);
      const { memoryBytesConstraint } = selectedProfileOption;
      const validMemoryConstraintMin = Number(memoryBytes) >= Number(memoryBytesConstraint.min);
      const validMemoryConstraintMax =
        !memoryBytesConstraint?.max || Number(memoryBytes) <= Number(memoryBytesConstraint?.max);

      return containsValues && validMemoryConstraintMin && validMemoryConstraintMax;
    } else {
      return Boolean(displayName && version);
    }
  }, [engineState, selectedProfileOption, versionKey, op]);

  const isConfigEditabilityDisabled =
    (selectedProfileOption as DAIEngineProfile)?.configEditability === DAIEngineProfile_ConfigEditability.DISABLED;

  const validateAdvancedConfiguration: KeyValuePairValidationFn = ({ key, value }): KeyValueValidation => {
    const message: KeyValueValidation = {};
    const { configEditability, configurationOverride } = selectedProfileOption as DAIEngineProfile;

    if (configEditability !== DAIEngineProfile_ConfigEditability.FULL) return message;

    if (configurationOverride?.hasOwnProperty(key || '')) {
      message.keyValidation = `"${key}" key already exists in the configuration_override.`;
    }
    if (!key) {
      message.keyValidation = 'The key may not be empty.';
    }
    if (!value) {
      message.valueValidation = 'The value may not be empty.';
    }

    return message;
  };

  return {
    isEngineValid,
    isConfigEditabilityDisabled,
    validateAdvancedConfiguration,
  };
};
